import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Button, Card, Empty, Spin, Tabs, Typography } from 'antd';
import { getMemberCurrentPlan } from '@/services/technical';
import { readCurrentPlan } from './currentPlan';
import CurrentPlanSchedule from './CurrentPlanSchedule';
import CurrentPlanTiming from './CurrentPlanTiming';
import CurrentPlanHistory from './CurrentPlanHistory';
import CurrentPlanAnalysisPanel from './CurrentPlanAnalysisPanel';
import TrainingAdvicePanel from './TrainingAdvicePanel';
import styles from './SimpleWorkspace.less';

function PlanTabLabel({ title, description }) {
  return (
    <span className={styles.planTabLabel}>
      <span className={styles.planTabTitle}>{title}</span>
      <span className={styles.planTabDescription}>{description}</span>
    </span>
  );
}

PlanTabLabel.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
};

export default function CurrentPlanPanel({ memberId }) {
  const [state, setState] = useState({ memberId: null, loading: true });
  const [revision, setRevision] = useState(0);
  const generation = useRef(0);
  useEffect(() => {
    const request = ++generation.current;
    const valid =
      Number.isSafeInteger(Number(memberId)) && Number(memberId) > 0;
    setState({
      memberId,
      loading: valid,
      error: valid ? '' : '会员信息不可用',
    });
    if (valid) {
      getMemberCurrentPlan({ requestData: { memberId } })
        .then((response) => {
          const data = readCurrentPlan(response, memberId);
          if (generation.current === request) setState({ memberId, data });
        })
        .catch((error) => {
          if (generation.current !== request) return;
          const status = error?.response?.status;
          setState({
            memberId,
            error:
              status === 401
                ? '请先登录后台'
                : status === 403
                ? '无会员档案菜单权限'
                : '在练计划暂时无法加载，请重试',
          });
        });
    }
    return () => {
      generation.current += 1;
    };
  }, [memberId, revision]);

  // 即便父组件未使用 key，切换会员的首次 render 也不展示上一个会员的计划。
  const current =
    String(state.memberId) === String(memberId) ? state : { loading: true };
  const { data, error, loading } = current;
  const progress =
    data?.total > 0 ? Math.round((data.completed / data.total) * 100) : null;
  return (
    <Card
      title="当前在练计划"
      size="small"
      className={`${styles.infoCard} ${styles.currentPlanCard}`}
      extra={
        <Button
          type="link"
          size="small"
          className={styles.planRefreshButton}
          disabled={!!loading}
          onClick={() => setRevision((n) => n + 1)}
        >
          刷新
        </Button>
      }
    >
      {loading ? (
        <Spin tip="加载在练计划…" />
      ) : error ? (
        <Alert type="warning" message={error} />
      ) : data?.status === 'EMPTY' ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="暂无当前在练计划"
        />
      ) : data ? (
        <>
          <div className={styles.planHero}>
            <div className={styles.planHeroHeading}>
              <div className={styles.planIdentity}>
                <span className={styles.planStatus}>
                  <span className={styles.planStatusDot} />
                  正在进行
                </span>
                <h3 className={styles.planName}>{data.name}</h3>
                <Typography.Text type="secondary">
                  {data.target
                    ? `围绕“${data.target}”持续训练`
                    : '会员当前正在执行的训练安排'}
                </Typography.Text>
              </div>
              <div className={styles.planProgressSummary}>
                <div className={styles.planProgressHeading}>
                  <Typography.Text type="secondary">完成进度</Typography.Text>
                  <Typography.Text className={styles.planProgressValue}>
                    {progress == null ? '暂不可用' : `${progress}%`}
                  </Typography.Text>
                </div>
                {progress != null && (
                  <div
                    className={styles.planProgressTrack}
                    role="progressbar"
                    aria-label={`计划完成 ${progress}%`}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={progress}
                  >
                    <span style={{ width: `${progress}%` }} />
                  </div>
                )}
                <Typography.Text type="secondary">
                  {data.total == null
                    ? '课程进度暂未提供'
                    : `已完成 ${data.completed} / ${data.total} 节`}
                </Typography.Text>
              </div>
            </div>
            <div className={styles.planFacts}>
              {[
                ['计划来源', data.source],
                ['计划类型', data.type],
                ['主训部位', data.bodyParts],
                ['计划难度', data.difficulty],
              ].map(([label, value]) => (
                <div key={label} className={styles.planFact}>
                  <span>{label}</span>
                  <strong>{value || '未提供'}</strong>
                </div>
              ))}
            </div>
          </div>
          {data.introduction && (
            <div className={styles.planIntroduction}>
              <div className={styles.planSectionHeading}>计划概述</div>
              <Typography.Paragraph
                type="secondary"
                ellipsis={{ rows: 3, expandable: true, symbol: '展开' }}
                className={styles.planIntroductionText}
              >
                {data.introduction}
              </Typography.Paragraph>
            </div>
          )}
          {data.currentPlanId ? (
            <Tabs
              className={styles.planContentTabs}
              defaultActiveKey="schedule"
              key={`${memberId}:${data.currentPlanId}`}
            >
              <Tabs.TabPane
                tab={
                  <PlanTabLabel
                    title="课程安排"
                    description="阶段、课程与动作参数"
                  />
                }
                key="schedule"
              >
                <CurrentPlanTiming
                  key={`timing:${memberId}:${revision}`}
                  memberId={memberId}
                />
                <CurrentPlanSchedule
                  key={`${memberId}:${data.currentPlanId}:${revision}`}
                  memberId={memberId}
                  currentPlanId={data.currentPlanId}
                />
              </Tabs.TabPane>
              <Tabs.TabPane
                tab={
                  <PlanTabLabel
                    title="计划分析"
                    description="判断计划是否适配"
                  />
                }
                key="analysis"
              >
                <CurrentPlanAnalysisPanel
                  key={`analysis:${memberId}:${data.currentPlanId}`}
                  memberId={memberId}
                  planId={data.currentPlanId}
                  courseType={0}
                  courseId={data.currentPlanId}
                />
              </Tabs.TabPane>
              <Tabs.TabPane
                tab={
                  <PlanTabLabel
                    title="训练建议"
                    description="调整方向与复评重点"
                  />
                }
                key="advice"
              >
                <TrainingAdvicePanel
                  key={`advice:${memberId}:${data.currentPlanId}`}
                  memberId={memberId}
                  planId={data.currentPlanId}
                />
              </Tabs.TabPane>
            </Tabs>
          ) : (
            <>
              <CurrentPlanTiming
                key={`timing:${memberId}:${revision}`}
                memberId={memberId}
              />
              <Typography.Paragraph type="secondary">
                未取得在练计划实例编号，暂不能发起整体分析。
              </Typography.Paragraph>
            </>
          )}
          <div className={styles.planFooter}>
            <Typography.Text
              type="secondary"
              className={styles.planReadOnlyHint}
            >
              与小程序首页当前在练计划对应；本处仅展示，不修改计划。
            </Typography.Text>
            <CurrentPlanHistory
              key={`history:${memberId}:${revision}`}
              memberId={memberId}
            />
          </div>
        </>
      ) : null}
    </Card>
  );
}

CurrentPlanPanel.propTypes = {
  memberId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};
CurrentPlanPanel.defaultProps = { memberId: null };
